/**
 * Centralized Error Handler with Humorous, Non-Technical Messages
 *
 * This module provides a comprehensive error handling system that converts
 * technical errors into friendly, humorous messages that users can actually understand.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/**
 * Humorous error messages categorized by error type
 */
var ERROR_MESSAGES = {
    network: {
        offline: "Looks like your internet took a coffee break ☕ Check your connection and try again!",
        slow: "Your internet is moving slower than a sloth on vacation 🦥 Hang tight!",
        server_down: "Our servers are having a little nap 😴 We'll wake them up soon!",
        generic: "The internet gremlins are at it again 👾 Give it another shot!"
    },
    auth: {
        invalid_credentials: "Invalid email or password. Please try again.",
        expired_session: "Your session has expired. Please sign in again.",
        unauthorized: "You do not have permission to perform this action.",
        email_not_verified: "Please verify your email address to continue.",
        weak_password: "Password does not meet security requirements.",
        email_taken: "An account with this email already exists."
    },
    validation: {
        required_field: "Looks like you forgot to fill in a spot 📝 We need all the details!",
        invalid_email: "That email address looks a bit wonky 📧 Mind double-checking it?",
        file_too_large: "That file is HUGE! 🐘 Our servers aren't quite ready for giants yet.",
        invalid_format: "That format doesn't play well with our system 🎭 Try something else!",
        empty_file: "This file is emptier than my wallet after shopping 💸 Got something with content?",
        missing_file: "File? What file? 🤔 Don't forget to actually upload something!"
    },
    file: {
        upload_failed: "The file upload went sideways 📁 It happens to the best of us!",
        processing_failed: "Our AI had a brain freeze while processing your file 🧠❄️ Let's try again!",
        corrupted: "This file seems to have gone through a blender 🌪️ Got a fresh copy?",
        unsupported_type: "Our system doesn't speak this file language yet 🗣️ Try a PDF!",
        virus_detected: "Whoa there! 🦠 This file looks suspicious. Safety first!",
        extraction_failed: "We couldn't extract the good stuff from this file 🔍 It's being stubborn!"
    },
    quota: {
        daily_limit: "You've reached your daily usage limit for this test site 🧪. Please contact support@lecturelearn.com to request an increase or discuss your needs!",
        monthly_limit: "Monthly quota reached for this test site 🧪. Contact support@lecturelearn.com to discuss your usage requirements!",
        file_size_limit: "That file is too large for the current test limits 🍔. Please contact support@lecturelearn.com for assistance!",
        api_limit: "Test site usage limit reached 🤖💤. Contact support@lecturelearn.com to continue testing!",
        storage_full: "Your storage is full on this test site 🦃. Contact support@lecturelearn.com for additional storage!"
    },
    timeout: {
        request_timeout: "That took longer than waiting for paint to dry 🎨 Let's try something smaller!",
        processing_timeout: "Our AI got distracted by something shiny ✨ Try breaking this into smaller pieces!",
        upload_timeout: "The upload is moving slower than a turtle with a backpack 🐢 Check your connection!",
        general_timeout: "Things are moving at snail pace today 🐌 Maybe try again in a bit?"
    },
    exam: {
        submission_failed: "Your exam submission went on a little adventure 🗺️ Don't worry, we'll find it!",
        grading_failed: "Our AI grader is having an existential crisis 🤖😵 Give it a moment!",
        session_expired: "Your exam session expired like old yogurt 🥛 Time for a fresh start!",
        invalid_answer: "That answer format is speaking a different language 🗣️ Try again!",
        no_questions: "We couldn't find any questions in there 🔍 Are you sure that's an exam?",
        already_submitted: "Hold your horses! 🐎 You already submitted this one!"
    },
    ai: {
        processing_failed: "Our AI had a little hiccup 🤖💫 Even robots need a second try sometimes!",
        model_unavailable: "The AI is out getting coffee ☕ It'll be back in a jiffy!",
        context_too_long: "That's more text than a novel! 📚 Try breaking it into smaller chunks.",
        generation_failed: "The AI's creativity meter is on empty 🎨 Let's try that again!",
        analysis_failed: "Our AI analyst called in sick today 🤒 But don't worry, it's feeling better now!"
    },
    generic: {
        unknown: "Something mysterious happened 🕵️‍♀️ Our tech wizards are on the case!",
        maintenance: "We're currently sprucing things up 🧹 Back in a flash!",
        rate_limited: "Slow down there, speed racer! 🏎️ Give us a second to catch up!",
        bad_request: "That request was a bit wonky 🤪 Mind trying again?",
        forbidden: "That's off-limits! 🚫 Like the last cookie in the jar.",
        not_found: "We looked everywhere but couldn't find that 🔍 Maybe it went on vacation?",
        json_parsing: "Our server got tongue-tied trying to speak JSON 😵 It happens to the best of us!",
        server_confused: "The server is having a moment of confusion 🤔 Let's give it a gentle nudge!"
    }
};
/**
 * Get a user-friendly error message based on error type and context
 */
export function getFriendlyErrorMessage(error, context, fallbackMessage) {
    var _a;
    if (context === void 0) { context = 'generic'; }
    // Handle JSON parsing errors early and consistently
    if (isJsonParsingError(error)) {
        return ERROR_MESSAGES.generic.json_parsing;
    }
    // Map common HTTP statuses to friendly messages first
    var status = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status;
    if (typeof status === 'number') {
        switch (status) {
            case 400:
                return ERROR_MESSAGES.generic.bad_request;
            case 401:
                return ERROR_MESSAGES.auth.expired_session;
            case 403:
                return ERROR_MESSAGES.generic.forbidden;
            case 404:
                return ERROR_MESSAGES.generic.not_found;
            case 413:
                return "Whoa there! 🐘 That file is too chunky for our servers. Mind trying something smaller?";
            case 415:
                return "Our system only speaks PDF fluently 📄 Try converting your file to PDF first!";
            case 429:
                // If limit type is known, tailor the message a bit
                if ((error === null || error === void 0 ? void 0 : error.limitType) === 'tokens')
                    return ERROR_MESSAGES.quota.daily_limit;
                if ((error === null || error === void 0 ? void 0 : error.limitType) === 'exam_analysis')
                    return ERROR_MESSAGES.quota.monthly_limit;
                return ERROR_MESSAGES.generic.rate_limited;
            case 500:
                return "Our server had a little meltdown 🤯 The hamsters need a coffee break! Try again in a moment.";
            case 502:
            case 503:
                return "Our servers are taking a quick power nap 😴 They'll be back online shortly!";
        }
    }
    // Try to determine error type from the error object
    var errorType = determineErrorType(error);
    var errorCode = extractErrorCode(error);
    // Get specific message based on context and error details
    var message = getSpecificErrorMessage(errorType, errorCode, context);
    // Fallback to provided message or generic
    if (!message) {
        message = fallbackMessage || ERROR_MESSAGES.generic.unknown;
    }
    return message;
}
/**
 * Determine error type from error object
 */
function determineErrorType(error) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (!error)
        return 'generic';
    // JSON parsing errors
    if (isJsonParsingError(error)) {
        return 'generic';
    }
    // Network errors
    if (error.code === 'NETWORK_ERROR' || ((_a = error.message) === null || _a === void 0 ? void 0 : _a.includes('network'))) {
        return 'network';
    }
    // Timeout errors
    if (error.code === 'ECONNABORTED' || ((_b = error.message) === null || _b === void 0 ? void 0 : _b.includes('timeout'))) {
        return 'timeout';
    }
    // Auth errors
    if (((_c = error.code) === null || _c === void 0 ? void 0 : _c.startsWith('auth/')) || ((_d = error.response) === null || _d === void 0 ? void 0 : _d.status) === 401) {
        return 'auth';
    }
    // File errors
    if (((_e = error.response) === null || _e === void 0 ? void 0 : _e.status) === 413 || ((_f = error.response) === null || _f === void 0 ? void 0 : _f.status) === 415) {
        return 'file';
    }
    // Quota errors
    if (((_g = error.response) === null || _g === void 0 ? void 0 : _g.status) === 429 || error.isLimitReached) {
        return 'quota';
    }
    // Validation errors
    if (((_h = error.response) === null || _h === void 0 ? void 0 : _h.status) === 400) {
        return 'validation';
    }
    return 'generic';
}
/**
 * Extract error code from various error formats
 */
function extractErrorCode(error) {
    var _a, _b, _c, _d;
    if (error.code)
        return error.code;
    if ((_b = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.code)
        return error.response.data.code;
    if ((_d = (_c = error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.error_code)
        return error.response.data.error_code;
    return '';
}
/**
 * Get specific error message based on type, code, and context
 */
function getSpecificErrorMessage(type, code, context) {
    var messages = ERROR_MESSAGES[type];
    // Handle JSON parsing errors specifically
    if (context.includes('JSON') || code.includes('JSON') ||
        context.includes('parsing') || code.includes('Expecting property name')) {
        return ERROR_MESSAGES.generic.json_parsing;
    }
    // Try context-specific mappings first
    if (context === 'auth') {
        switch (code) {
            case 'auth/user-not-found':
            case 'auth/wrong-password':
                return ERROR_MESSAGES.auth.invalid_credentials;
            case 'auth/email-already-in-use':
                return ERROR_MESSAGES.auth.email_taken;
            case 'auth/weak-password':
                return ERROR_MESSAGES.auth.weak_password;
            case 'auth/user-token-expired':
                return ERROR_MESSAGES.auth.expired_session;
        }
    }
    if (context === 'file' || context === 'upload') {
        switch (code) {
            case 'FILE_TOO_LARGE':
                return ERROR_MESSAGES.file.upload_failed;
            case 'UNSUPPORTED_FILE_TYPE':
                return ERROR_MESSAGES.file.unsupported_type;
            case 'FILE_PROCESSING_FAILED':
                return ERROR_MESSAGES.file.processing_failed;
        }
    }
    if (context === 'quota' || context === 'limit') {
        switch (code) {
            case 'DAILY_TOKENS_LIMIT_REACHED':
                return ERROR_MESSAGES.quota.daily_limit;
            case 'EXAM_ANALYSIS_LIMIT_REACHED':
                return ERROR_MESSAGES.quota.monthly_limit;
        }
    }
    // Default to first message in category
    var messageKeys = Object.keys(messages);
    if (messageKeys.length > 0) {
        return messages[messageKeys[0]];
    }
    return ERROR_MESSAGES.generic.server_confused;
}
/**
 * Create a structured error object
 */
export function createAppError(error, context, options) {
    var _a;
    if (context === void 0) { context = 'generic'; }
    if (options === void 0) { options = {}; }
    var errorType = determineErrorType(error);
    var message = options.customMessage || getFriendlyErrorMessage(error, context);
    return {
        id: generateErrorId(),
        message: message,
        originalError: error,
        type: errorType,
        severity: determineSeverity(error),
        recoverable: isRecoverable(error),
        retryable: (_a = options.retryable) !== null && _a !== void 0 ? _a : isRetryable(error),
        timestamp: new Date()
    };
}
/**
 * Handle error with logging and user notification
 */
export function handleError(error, context, options) {
    if (context === void 0) { context = 'generic'; }
    if (options === void 0) { options = {}; }
    var appError = createAppError(error, context, options);
    // Log to console if enabled (default: true in dev, false in prod)
    if (options.logToConsole !== false) {
        console.error("[".concat(appError.type.toUpperCase(), "] ").concat(context, ":"), {
            message: appError.message,
            originalError: error,
            timestamp: appError.timestamp
        });
    }
    return appError;
}
/**
 * Helper to detect common JSON parsing error signatures
 */
export function isJsonParsingError(error) {
    var msg = (error === null || error === void 0 ? void 0 : error.message) || '';
    return typeof msg === 'string' && (msg.includes('JSON') ||
        msg.includes('Expecting property name') ||
        msg.includes('Unexpected token') ||
        msg.includes('SyntaxError'));
}
/**
 * Utility functions
 */
function generateErrorId() {
    return "err_".concat(Date.now(), "_").concat(Math.random().toString(36).substr(2, 9));
}
function determineSeverity(error) {
    var _a, _b, _c;
    if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) >= 500)
        return 'high';
    if (((_b = error.response) === null || _b === void 0 ? void 0 : _b.status) === 401 || ((_c = error.response) === null || _c === void 0 ? void 0 : _c.status) === 403)
        return 'medium';
    return 'low';
}
function isRecoverable(error) {
    var _a, _b, _c, _d;
    // Network errors are usually recoverable
    if (error.code === 'NETWORK_ERROR')
        return true;
    // Timeout errors are recoverable
    if (error.code === 'ECONNABORTED')
        return true;
    // 4xx errors (except auth) are usually not recoverable
    if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) >= 400 && ((_b = error.response) === null || _b === void 0 ? void 0 : _b.status) < 500) {
        return ((_c = error.response) === null || _c === void 0 ? void 0 : _c.status) === 429; // Rate limit is recoverable
    }
    // 5xx errors are usually recoverable
    if (((_d = error.response) === null || _d === void 0 ? void 0 : _d.status) >= 500)
        return true;
    return false;
}
function isRetryable(error) {
    var _a, _b;
    // Network errors are retryable
    if (error.code === 'NETWORK_ERROR')
        return true;
    // Timeout errors are retryable
    if (error.code === 'ECONNABORTED')
        return true;
    // Rate limit errors are retryable (after delay)
    if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 429)
        return true;
    // Server errors are retryable
    if (((_b = error.response) === null || _b === void 0 ? void 0 : _b.status) >= 500)
        return true;
    return false;
}
/**
 * Error boundary helper for React components
 */
var ErrorBoundaryHelper = /** @class */ (function () {
    function ErrorBoundaryHelper() {
    }
    ErrorBoundaryHelper.getDerivedStateFromError = function (error) {
        return {
            hasError: true,
            error: createAppError(error, 'react_error', {
                customMessage: "Oops! Something went a bit wonky 🤯 Don't worry, we're on it!"
            })
        };
    };
    ErrorBoundaryHelper.logErrorInfo = function (error, errorInfo) {
        console.error('React Error Boundary caught an error:', {
            error: error,
            errorInfo: errorInfo,
            timestamp: new Date().toISOString()
        });
    };
    return ErrorBoundaryHelper;
}());
export { ErrorBoundaryHelper };
/**
 * Hook for handling async operations with error handling
 */
export function createAsyncErrorHandler(context) {
    var _this = this;
    if (context === void 0) { context = 'async_operation'; }
    return {
        handleAsync: function (operation, options) {
            if (options === void 0) { options = {}; }
            return __awaiter(_this, void 0, void 0, function () {
                var data, error_1, appError;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            _a.trys.push([0, 2, , 3]);
                            return [4 /*yield*/, operation()];
                        case 1:
                            data = _a.sent();
                            return [2 /*return*/, { data: data }];
                        case 2:
                            error_1 = _a.sent();
                            appError = handleError(error_1, context, options);
                            return [2 /*return*/, { error: appError }];
                        case 3: return [2 /*return*/];
                    }
                });
            });
        }
    };
}
export default {
    getFriendlyErrorMessage: getFriendlyErrorMessage,
    createAppError: createAppError,
    handleError: handleError,
    isJsonParsingError: isJsonParsingError,
    ErrorBoundaryHelper: ErrorBoundaryHelper,
    createAsyncErrorHandler: createAsyncErrorHandler
};
